import { useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import { PageDTO, useDeletePageApi, useSavePageApi, useSearchPageApi } from '../../../api/page-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { CreatedRowInfo } from '../../../components/table/created-row-info';
import { ModifiedRowInfo } from '../../../components/table/modified-row-info';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SavePageModal } from './save-page-modal';

export function PageApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedPage, setSelectedPage] = useState<PageDTO | undefined>();
    const pageInit = useSearchPageApi();
    const savePage = useSavePageApi();
    const deletePage = useDeletePageApi();

    const { result, error, busy, initialized, initializing } = pageInit;

    const columns: IBasicTableColumn<PageDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 150,
                Cell: ({ row }) => <N>{row.id}</N>,
            },
            {
                Header: <T>title</T>,
                name: 'title',
            },

            {
                Header: <T>created-by</T>,
                name: 'createdBy',
                width: 120,
                Cell: CreatedRowInfo,
            },
            {
                Header: <T>modified-by</T>,
                name: 'modifiedAt',
                width: 120,
                Cell: ModifiedRowInfo,
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        pageInit.post(null, {});
    }, [initialized, initializing, error, pageInit]);

    const handleDelete = (page: PageDTO) => {
        bd.msgbox(
            translate('delete-page'),
            translate('confirm-delete-page'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deletePage.remove(
                            page,
                            () => {
                                pageInit.updateResult({
                                    ...result,
                                    pages: result.pages.filter(b => b.id !== page.id),
                                });
                                notify.dark(translate('selected-page-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedPage(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (page: PageDTO) => {
        savePage.post(
            null,
            page,
            res => {
                if (selectedPage) {
                    pageInit.updateResult({
                        ...result,
                        pages: result.pages.map(b => (b.id === page.id ? res.page : b)),
                    });
                } else {
                    pageInit.updateResult({
                        ...result,
                        pages: [res.page, ...result.pages],
                    });
                }

                setShowModal(false);
                setSelectedPage(undefined);
                notify.success(
                    selectedPage
                        ? translate('selected-page-updated-successfully')
                        : translate('new-page-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => pageInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="pages"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-page</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedPage(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.pages}
                        onRowClick={({ row }) => {
                            setSelectedPage(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SavePageModal
                        show={showModal}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedPage={selectedPage}
                        allowEdit={result.allowEdit}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedPage(undefined);
                        }}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
